import type { ManagedCustomer } from '@/lib/business-workflow'
import { requestApiEnvelope } from '@/lib/data-client/http'
import { formatTaiwanAddressAnswer } from '@/lib/taiwan-address'
import type { AnswerValue, Question } from '@/types/form'

export type NewCustomerSubmitDuplicateMatchField = 'taxId' | 'companyPhone' | 'contactPhone'

export type NewCustomerSubmitDuplicatePreviewStatus =
  | 'not_found'
  | 'erp_customer_exists'
  | 'project_customer_exists'
  | 'pending_questionnaire_exists'

export type NewCustomerSubmitDuplicatePreviewRequest = {
  enabled: boolean
  taxId: string
  companyPhone: string
  contactPhone: string
  submittedCustomer: Partial<ManagedCustomer>
  answers: Array<{
    questionId: string
    questionTitle: string
    questionType: Question['type']
    mappedField: string
    answer: string
  }>
}

export type NewCustomerSubmitDuplicatePreviewContract = {
  status: NewCustomerSubmitDuplicatePreviewStatus
  message: string
  matchedFields: NewCustomerSubmitDuplicateMatchField[]
  shouldBlockSubmit: boolean
  requiresComparison: boolean
  customer?: ManagedCustomer
  pendingRecord?: NewCustomerSubmitPendingQuestionnaireRecord
  archiveTargets?: NewCustomerSubmitDuplicateArchiveTarget[]
}

export type NewCustomerSubmitPendingQuestionnaireAnswer = {
  questionId: string
  questionTitle: string
  questionType: Question['type'] | string
  mappedField: string
  answer: string
}

export type NewCustomerSubmitPendingQuestionnaireRecord = {
  id: string
  responseId: string
  applicationId: string
  submittedAt: string
  answers: NewCustomerSubmitPendingQuestionnaireAnswer[]
}

export type NewCustomerSubmitDuplicateArchiveTarget = {
  sourceRecordId: string
  responseId: string
  applicationId: string
}

export type NewCustomerSubmitDuplicateComparisonRow = {
  field: string
  label: string
  submittedValue: string
  customerValue: string
  status: 'same' | 'different'
  statusLabel: '相同' | '不同'
}

type SubmitComparisonConfig = {
  field: string
  label: string
  submittedTargets: string[]
  submittedTitles: string[]
  getCustomerValue: (customer: ManagedCustomer) => unknown
}

const fullNewCustomerFlowAnswer = '全新客戶'

const submitComparisonFields: SubmitComparisonConfig[] = [
  answerConfig('customerSource', '新客戶來源', ['customerSource'], ['新客戶來源']),
  customerConfig('companyName', '客戶簡稱', ['customerShortName', 'companyName'], ['客戶簡稱'], (customer) => customer.companyName),
  answerConfig('responsiblePerson', '負責人', ['ownerName'], ['負責人']),
  customerConfig('contactName', '聯絡人', ['contactName'], ['聯絡人'], (customer) => customer.contactName),
  customerConfig('companyPhone', '市話', ['phone1', 'companyPhone'], ['市話'], (customer) => customer.companyPhone),
  customerConfig('contactPhone', '手機', ['phone2', 'contactPhone'], ['手機'], (customer) => customer.contactPhone),
  answerConfig('fax', '傳真號碼', ['fax'], ['傳真號碼']),
  customerConfig('contactEmail', 'E-Mail', ['email', 'contactEmail'], ['E-Mail'], (customer) => customer.contactEmail),
  customerConfig('taxId', '統一編號', ['taxId'], ['統一編號'], (customer) => customer.taxId),
  answerConfig('companyFullName', '統編抬頭（公司全名）', ['companyFullName'], ['統編抬頭（公司全名）']),
  answerConfig('taxIdMissingReason', '沒有統編的原因', ['taxIdMissingReason', 'noTaxIdReason', 'no_tax_id_reason'], ['沒有統編的原因']),
  customerConfig('companySize', '員工人數', ['employeeCountRange', 'companySize'], ['員工人數'], (customer) => customer.companySize),
  customerConfig('companyAddress', '送貨地址', ['deliveryAddress', 'companyAddress'], ['送貨地址'], (customer) => customer.companyAddress),
  answerConfig('invoiceAddress', '發票地址', ['invoiceAddress'], ['發票地址']),
  answerConfig('registeredAddress', '登記地址', ['registeredAddress'], ['登記地址']),
  answerConfig('billingEmail', '帳單收件Mail', ['billingEmail'], ['帳單收件Mail']),
  answerConfig('deliveryTimeNote', '可收貨時間', ['deliveryTimeNote'], ['可收貨時間']),
  answerConfig('businessHours', '營業時段、公休日', ['businessHours'], ['營業時段、公休日']),
  customerConfig('departmentName', '部門別', ['department', 'departmentName'], ['部門別'], (customer) => customer.departmentName),
  answerConfig(
    'salesRep',
    '業務',
    ['salesRepSpecial', 'salesRepDiv1', 'salesRepDiv2', 'salesRepDiv3', 'salesRepSpecialCh', 'salesRepDiv4'],
    ['特販業務', '營一業務', '營二業務', '營三業務', '特通業務', '營四業務', '業務'],
  ),
  answerConfig('customerCategory', '客戶類別', ['customerCategory'], ['客戶類別']),
  customerConfig('industry', '客戶通路別', ['customerChannel', 'industry'], ['客戶通路別'], (customer) => customer.industry),
  customerConfig('customerType', '型態別', ['customerType'], ['型態別'], (customer) => customer.customerType),
  customerConfig('salesRegion', '客戶區域別', ['region', 'salesRegion', 'districtTaipei', 'districtNewTaipei', 'districtTaichung', 'districtTainan', 'districtKaohsiung'], ['客戶區域別'], (customer) => customer.salesRegion),
  answerConfig('remark', '備註', ['remark'], ['備註']),
  answerConfig('submittedBy', '填單人員', ['submittedBy'], ['填單人員']),
  answerConfig('foodBusinessRegNo', '食品業者登入字號', ['foodBusinessRegNo'], ['食品業者登入字號']),
]

export function shouldHideCustomerShortNameDuplicateCheckButton(
  question: Question,
  questions: Question[],
  answers: Map<string, AnswerValue>,
) {
  return (
    question.settings.customerDuplicateCheck?.field === 'customer_short_name' &&
    isFullNewCustomerFlow(questions, answers)
  )
}

export function buildNewCustomerSubmitDuplicatePreviewRequest(
  questions: Question[],
  answers: Map<string, AnswerValue>,
): NewCustomerSubmitDuplicatePreviewRequest {
  const submittedCustomer = buildSubmittedCustomerPatch(questions, answers)
  const enabled =
    isFullNewCustomerFlow(questions, answers) &&
    Boolean(
      normalizeComparable(submittedCustomer.taxId) ||
        normalizePhone(submittedCustomer.companyPhone) ||
        normalizePhone(submittedCustomer.contactPhone),
    )

  return {
    enabled,
    taxId: formatValue(submittedCustomer.taxId),
    companyPhone: formatValue(submittedCustomer.companyPhone),
    contactPhone: formatValue(submittedCustomer.contactPhone),
    submittedCustomer,
    answers: buildSubmittedAnswerRecords(questions, answers),
  }
}

export function createNewCustomerSubmitDuplicateComparisonRows(
  questions: Question[],
  answers: Map<string, AnswerValue>,
  comparedCustomer: ManagedCustomer,
): NewCustomerSubmitDuplicateComparisonRow[] {
  return submitComparisonFields.map((config) => {
    const submittedValue = getSubmittedComparisonValue(config, questions, answers)
    const customerValue = getCustomerComparisonValue(config, comparedCustomer)
    const status =
      normalizeComparable(submittedValue) === normalizeComparable(customerValue)
        ? 'same'
        : 'different'
    return {
      field: config.field,
      label: config.label,
      submittedValue,
      customerValue,
      status,
      statusLabel: status === 'same' ? '相同' : '不同',
    }
  })
}

export function createNewCustomerPendingQuestionnaireComparisonRows(
  questions: Question[],
  answers: Map<string, AnswerValue>,
  pendingRecord: NewCustomerSubmitPendingQuestionnaireRecord,
): NewCustomerSubmitDuplicateComparisonRow[] {
  return submitComparisonFields.map((config) => {
    const submittedValue = getSubmittedComparisonValue(config, questions, answers)
    const customerValue = getPendingQuestionnaireComparisonValue(config, pendingRecord)
    const status =
      normalizeComparable(submittedValue) === normalizeComparable(customerValue)
        ? 'same'
        : 'different'
    return {
      field: config.field,
      label: config.label,
      submittedValue,
      customerValue,
      status,
      statusLabel: status === 'same' ? '相同' : '不同',
    }
  })
}

export async function fetchNewCustomerSubmitDuplicatePreview({
  formId,
  request,
}: {
  formId: string
  request: NewCustomerSubmitDuplicatePreviewRequest
}) {
  const envelope = await requestApiEnvelope<NewCustomerSubmitDuplicatePreviewContract>(
    `/api/public/forms/${encodeURIComponent(formId)}/new-customer-duplicate-preview`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  )
  return envelope.data
}

export async function fetchNewCustomerPendingQuestionnaireDuplicatePreview({
  formId,
  request,
}: {
  formId: string
  request: NewCustomerSubmitDuplicatePreviewRequest
}) {
  const envelope = await requestApiEnvelope<NewCustomerSubmitDuplicatePreviewContract>(
    `/api/public/forms/${encodeURIComponent(formId)}/pending-questionnaire-duplicate-preview`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  )
  return envelope.data
}

export function clearNewCustomerFormAnswers(
  questions: Question[],
  answers: Map<string, AnswerValue>,
) {
  const nextAnswers = new Map(answers)
  for (const question of questions) {
    if (isFullNewCustomerClearableQuestion(question)) nextAnswers.delete(question.id)
  }
  return nextAnswers
}

function isFullNewCustomerFlow(questions: Question[], answers: Map<string, AnswerValue>) {
  const formTypeQuestion = questions.find(
    (question) =>
      normalizeKey(question.settings.customerFieldTarget) === 'formtype' ||
      normalizeKey(question.title) === normalizeKey('新客戶建檔選擇'),
  )
  const value = formTypeQuestion ? answers.get(formTypeQuestion.id) : undefined
  return normalizeKey(value).includes(normalizeKey(fullNewCustomerFlowAnswer))
}

function buildSubmittedCustomerPatch(
  questions: Question[],
  answers: Map<string, AnswerValue>,
): Partial<ManagedCustomer> {
  const get = (targets: string[], titles: string[]) =>
    getSubmittedAnswerByTargetOrTitle(questions, answers, targets, titles)

  return {
    companyName: get(['customerShortName', 'companyName'], ['客戶簡稱']),
    taxId: get(['taxId'], ['統一編號']),
    industry: get(['customerChannel', 'industry'], ['客戶通路別']),
    customerType: get(['customerType'], ['型態別']),
    departmentName: get(['department', 'departmentName'], ['部門別']),
    companySize: get(['employeeCountRange', 'companySize'], ['員工人數']),
    companyAddress: get(['deliveryAddress', 'companyAddress'], ['送貨地址']),
    companyPhone: get(['phone1', 'companyPhone'], ['市話']),
    contactName: get(['contactName'], ['聯絡人']),
    contactEmail: get(['email', 'contactEmail'], ['E-Mail']),
    contactPhone: get(['phone2', 'contactPhone'], ['手機']),
    salesRegion: get(
      ['region', 'salesRegion', 'districtTaipei', 'districtNewTaipei', 'districtTaichung', 'districtTainan', 'districtKaohsiung'],
      ['客戶區域別'],
    ),
  }
}

function buildSubmittedAnswerRecords(questions: Question[], answers: Map<string, AnswerValue>) {
  return questions
    .map((question) => ({
      questionId: question.id,
      questionTitle: question.title,
      questionType: question.type,
      mappedField: getQuestionMappedField(question),
      answer: formatValue(answers.get(question.id)),
    }))
    .filter((answer) => answer.answer.trim().length > 0)
}

function getSubmittedComparisonValue(
  config: SubmitComparisonConfig,
  questions: Question[],
  answers: Map<string, AnswerValue>,
) {
  return getSubmittedAnswerByTargetOrTitle(
    questions,
    answers,
    config.submittedTargets,
    config.submittedTitles,
  )
}

function getCustomerComparisonValue(config: SubmitComparisonConfig, customer: ManagedCustomer) {
  const directValue = formatValue(config.getCustomerValue(customer))
  if (directValue) return directValue
  const answer = customer.answers.find(
    (item) =>
      config.submittedTargets.some((target) => normalizeKey(item.mappedField) === normalizeKey(target)) ||
      config.submittedTitles.some((title) => normalizeKey(item.questionTitle) === normalizeKey(title)),
  )
  return formatValue(answer?.answer)
}

function getPendingQuestionnaireComparisonValue(
  config: SubmitComparisonConfig,
  pendingRecord: NewCustomerSubmitPendingQuestionnaireRecord,
) {
  const answer = pendingRecord.answers.find(
    (item) =>
      config.submittedTargets.some((target) => normalizeKey(item.mappedField) === normalizeKey(target)) ||
      config.submittedTitles.some((title) => normalizeKey(item.questionTitle) === normalizeKey(title)),
  )
  return formatValue(answer?.answer)
}

function getSubmittedAnswerByTargetOrTitle(
  questions: Question[],
  answers: Map<string, AnswerValue>,
  targets: string[],
  titles: string[],
) {
  const question = questions.find(
    (item) =>
      targets.some((target) => normalizeKey(getQuestionMappedField(item)) === normalizeKey(target)) ||
      titles.some((title) => normalizeKey(item.title) === normalizeKey(title)),
  )
  return question ? formatValue(answers.get(question.id)) : ''
}

function isFullNewCustomerClearableQuestion(question: Question) {
  const mappedField = normalizeKey(getQuestionMappedField(question))
  if (mappedField === 'formtype') return false
  return submitComparisonFields.some((config) =>
    config.submittedTargets.some((target) => normalizeKey(target) === mappedField),
  )
}

function getQuestionMappedField(question: Question) {
  const configuredTarget = question.settings.customerFieldTarget
  if (typeof configuredTarget === 'string' && configuredTarget.trim()) return configuredTarget.trim()
  const dbFieldName = question.settings.dbFieldName
  return typeof dbFieldName === 'string' ? dbFieldName.trim() : ''
}

function customerConfig(
  field: string,
  label: string,
  submittedTargets: string[],
  submittedTitles: string[],
  getCustomerValue: (customer: ManagedCustomer) => unknown,
): SubmitComparisonConfig {
  return {
    field,
    label,
    submittedTargets,
    submittedTitles,
    getCustomerValue,
  }
}

function answerConfig(
  field: string,
  label: string,
  submittedTargets: string[],
  submittedTitles: string[],
): SubmitComparisonConfig {
  return customerConfig(field, label, submittedTargets, submittedTitles, () => '')
}

function formatValue(value: unknown) {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.map(String).filter(Boolean).join('、')
  if (typeof value === 'object') {
    const address = formatTaiwanAddressAnswer(value)
    if (address) return address
    return Object.entries(value)
      .filter(([, item]) => item !== undefined && item !== null && item !== '')
      .map(([key, item]) => `${key}: ${String(item)}`)
      .join('；')
  }
  return String(value).trim()
}

function normalizeComparable(value: unknown) {
  return formatValue(value).toLocaleLowerCase().replace(/\s+/g, '')
}

function normalizePhone(value: unknown) {
  return formatValue(value).replace(/[^\d+]/g, '')
}

function normalizeKey(value: unknown) {
  return String(value ?? '')
    .trim()
    .toLocaleLowerCase()
    .replace(/[\s_（）()，,、/-]+/g, '')
}
